import axios from 'axios';
import { designerItemsAPI } from './designerItemsAPI';
import { getMarketplaceApiBaseUrl } from './resolveApiBaseUrl';
import { sortBrowseItems } from '../utils/sortBrowseItems';

const API_BASE_URL =
  process.env.REACT_APP_ITEMS_API_URL || getMarketplaceApiBaseUrl() || 'http://localhost:8080/api';

const emptyPage = (page, pageSize) => ({
  results: [],
  count: 0,
  page,
  page_size: pageSize,
  total_pages: 0,
  has_next: false,
});

function normalizeMarketplaceItem(item) {
  const images = item.images || [];
  const primary = item.primary_image || item.image_url || (images[0]?.image ?? images[0]);
  return {
    key: `item-${item.id}`,
    listingKind: 'item',
    id: item.id,
    title: item.title,
    price: item.price,
    category: item.category,
    condition: item.condition,
    brand_name: item.brand_name ?? null,
    custom_brand_name: item.custom_brand_name ?? null,
    is_no_brand: item.is_no_brand ?? null,
    item_code: item.item_code,
    images,
    image_url: primary,
    brand_logo: null,
    created_at: item.created_at,
    distance_km: item.distance_km ?? null,
  };
}

function normalizeDesignerItem(item) {
  const title = item.title || `${item.brand_name || ''} ${item.item_name || ''}`.trim() || 'Listing';
  const images = item.images || [];
  const primary = item.primary_image || (images[0]?.image ?? images[0]);
  return {
    key: `designer_item-${item.id}`,
    listingKind: 'designer_item',
    id: item.id,
    title,
    price: item.price,
    category: item.category,
    condition: item.condition,
    brand_name: item.brand_name ?? null,
    custom_brand_name: item.custom_brand_name ?? null,
    is_no_brand: item.is_no_brand ?? null,
    item_code: item.item_code,
    images,
    image_url: primary,
    brand_logo: item.brand_logo,
    created_at: item.created_at,
    distance_km: item.distance_km ?? null,
  };
}

async function getMarketplaceLuxuryPage(filters, page, pageSize) {
  const params = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (filters[key]) params.append(key, String(filters[key]));
  });
  params.append('is_luxury', 'true');
  params.append('page', String(page));
  params.append('page_size', String(pageSize));
  const response = await axios.get(`${API_BASE_URL}/public/items/?${params.toString()}`);
  const data = response.data;
  if (Array.isArray(data)) {
    return {
      results: data.map(normalizeMarketplaceItem),
      count: data.length,
      page: 1,
      page_size: data.length,
      has_next: false,
    };
  }
  return { ...data, results: (data.results || []).map(normalizeMarketplaceItem) };
}

async function fetchDesignerPage(filters, page, pageSize) {
  try {
    const payload = await designerItemsAPI.getDesignerItemsPage(filters, page, pageSize);
    const rows = (payload?.results || []).map(normalizeDesignerItem);
    return { rows, has_next: Boolean(payload?.has_next), count: payload?.count || rows.length };
  } catch {
    return { rows: [], has_next: false, count: 0 };
  }
}

/** Luxury hub browse — designer listings plus pre-owned luxury marketplace pieces. */
export const luxuryBrowseAPI = {
  getLuxuryListingsPage: async (filters = {}, page = 1, pageSize = 24, sort = '') => {
    const apiFilters = { ...filters };
    delete apiFilters.view;
    if (apiFilters.subcategory && apiFilters.category === 'Books') {
      apiFilters.book_subcategory = apiFilters.subcategory;
    }
    const effectiveSort = sort === 'nearest' ? 'recommended' : sort;
    if (effectiveSort && effectiveSort !== 'recommended') {
      apiFilters.sort = effectiveSort;
    }
    const perSource = Math.max(6, Math.ceil(pageSize / 2));

    const [designerRes, marketRes] = await Promise.all([
      fetchDesignerPage(apiFilters, page, perSource),
      getMarketplaceLuxuryPage(apiFilters, page, perSource).catch(() => emptyPage(page, perSource)),
    ]);

    const merged = sortBrowseItems([...designerRes.rows, ...(marketRes.results || [])], sort || 'recommended');
    const has_next = Boolean(designerRes.has_next || marketRes.has_next);

    return {
      results: merged,
      count: merged.length,
      page,
      page_size: pageSize,
      total_pages: has_next ? page + 1 : page,
      has_next,
    };
  },
};
